import { useLazyQuery, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { parseTimestampField } from "../../classes/utils/DateUtils";
import { GET_CLINICAL_HISTORIES_BY_IDS } from "../../queries/clinicalHistoryQueries";
import { GET_PATIENT_CONDITIONS_BY_IDS } from "../../queries/patientConditionQueries";
import { GET_QUALITY_OF_LIFE_INDICATORS_BY_IDS } from "../../queries/qualityOfLifeIndicatorQueries";

/**
 * Hook to fetch and manage clinical history data
 * @param {string[]} clinicalHistoryIds - Array of clinical history IDs
 * @returns {object} Clinical history data and utilities
 */
export function useClinicalHistoryData(clinicalHistoryIds) {
  const [populatedHistories, setPopulatedHistories] = useState([]);

  const { data, loading, error, refetch } = useQuery(
    GET_CLINICAL_HISTORIES_BY_IDS,
    {
      variables: { ids: clinicalHistoryIds },
      skip: !clinicalHistoryIds || clinicalHistoryIds.length === 0,
      fetchPolicy: "network-only",
    }
  );

  const [getConditions] = useLazyQuery(GET_PATIENT_CONDITIONS_BY_IDS);
  const [getQualityIndicators] = useLazyQuery(GET_QUALITY_OF_LIFE_INDICATORS_BY_IDS);

  const clinicalHistories = useMemo(
    () => data?.clinicalHistoriesByIds || [],
    [data]
  );

  // Fetch conditions for all histories
  useEffect(() => {
    const populateConditions = async () => {
      if (!clinicalHistories.length) {
        setPopulatedHistories([]);
        return;
      }

      // Collect all condition IDs
      const conditionIds = clinicalHistories
        .map(h => h.conditions)
        .filter(Boolean);

      // Collect all QoL indicator IDs (standard + custom)
      const qualityIndicatorIds = clinicalHistories
        .flatMap(h => [
          ...(h.quality_of_life_indicators || []),
          ...(h.custom_quality_of_life_indicators || []),
        ])
        .filter(Boolean);

      try {
        // Fetch conditions
        const conditionsPromise = conditionIds.length
          ? getConditions({ variables: { ids: conditionIds } })
          : Promise.resolve({ data: { patientConditionsByIds: [] } });

        // Fetch QoL indicators
        const qualityIndicatorsPromise = qualityIndicatorIds.length
          ? getQualityIndicators({ variables: { ids: qualityIndicatorIds } })
          : Promise.resolve({ data: { qualityOfLifeIndicatorsByIds: [] } });

        const [{ data: conditionsData }, { data: qualityIndicatorsData }] =
          await Promise.all([conditionsPromise, qualityIndicatorsPromise]);

        // Map conditions by ID for quick lookup
        const conditionsMap = {};
        conditionsData?.patientConditionsByIds?.forEach(cond => {
          conditionsMap[cond._id] = cond.conditions; // array of condition names
        });

        const qualityIndicatorsMap = {};
        qualityIndicatorsData?.qualityOfLifeIndicatorsByIds?.forEach(qol => {
          qualityIndicatorsMap[qol._id] = qol;
        });

        // Populate histories with condition names and QoL indicator details
        const populated = clinicalHistories.map(history => ({
          ...history,
          conditionsData: conditionsMap[history.conditions] || [],
          qualityOfLifeIndicatorsData: [
            ...(history.quality_of_life_indicators || []),
            ...(history.custom_quality_of_life_indicators || []),
          ]
            .map(id => {
              const indicator = qualityIndicatorsMap[id];
              if (!indicator) return null;
              const isCustom =
                indicator.is_custom ||
                (history.custom_quality_of_life_indicators || []).includes(id);
              return { ...indicator, is_custom: isCustom };
            })
            .filter(Boolean),
        }));

        setPopulatedHistories(populated);
      } catch (err) {
        console.error("Error loading clinical history relations:", err);
        setPopulatedHistories(clinicalHistories);
      }
    };

    populateConditions();
  }, [clinicalHistories, getConditions]);

  const historiesByDate = useMemo(() => {
    const getTimestamp = history => {
      const parsed = parseTimestampField(history?.createdAt || history?.updatedAt);
      return parsed ? parsed.getTime() : 0;
    };

    return [...populatedHistories].sort(
      (a, b) => getTimestamp(b) - getTimestamp(a)
    );
  }, [populatedHistories]);

  const latestHistory = useMemo(
    () => historiesByDate[0] || null,
    [historiesByDate]
  );

  return {
    clinicalHistories: populatedHistories,
    historiesByDate,
    latestHistory,
    loading,
    error,
    refreshClinicalHistories: refetch,
  };
}
